import { Component, HostBinding, OnInit, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { NavHeaderService } from '../../shared/services/nav-header.service';
import { NavService } from '../../features/nav/nav.service';
import { HelpPanelService } from '../../shared/components/help-panel/help-panel.service';
import { AuthService } from '../../features/auth/auth.service';
import { UiService } from '../../core/services/ui.service';
import { PaymentBannerService } from '../../shared/services/payment-banner.service';
import { Router } from '@angular/router';

@Component({
    selector: 'app-main-layout',
    templateUrl: './main-layout.component.html',
    styleUrls: ['./main-layout.component.scss'],
    standalone: false
})
export class MainLayoutComponent implements OnInit {
  showNavigation$!: Observable<boolean>;
  isSideNavCollapsed = false;
  private readonly authService = inject(AuthService);
  protected readonly ui = inject(UiService);
  private readonly paymentBanner = inject(PaymentBannerService);
  private readonly router = inject(Router);

  constructor(
    private navHeaderService: NavHeaderService,
    private navService: NavService,
    private helpPanelService: HelpPanelService
  ) {}

  get isHelpPanelOpen(): boolean {
    return this.helpPanelService.isOpen();
  }

  get helpPanelWidth(): number {
    return this.helpPanelService.panelWidth();
  }

  ngOnInit() {
    this.showNavigation$ = this.navHeaderService.showNavHeaderObs$;
    
    this.navService.isSideNavCollapsed$.subscribe((isCollapsed) => {
      this.isSideNavCollapsed = isCollapsed;
    });
  }

  @HostBinding('class.has-impersonation-banner')
  get hasBanner(): boolean {
    return this.showImpersonationBanner || this.showPaymentBanner;
  }

  @HostBinding('class.has-two-banners')
  get hasTwoBanners(): boolean {
    return this.showImpersonationBanner && this.showPaymentBanner;
  }

  get showPaymentBanner(): boolean {
    return this.paymentBanner.noStripeAccount();
  }

  get paymentBannerMessage(): string | null {
    return this.paymentBanner.message();
  }

  goToStripeSetup(): void {
    this.router.navigateByUrl('/main/setup/stripe');
  }

  get showImpersonationBanner(): boolean {
    return localStorage.getItem('superUser') === 'true';
  }

  get isImpersonating(): boolean {
    return !!(localStorage.getItem('impersonatedName') || localStorage.getItem('impersonatedEmail'));
  }


  exitImpersonation(): void {
    localStorage.removeItem('impersonatedEmail');
    localStorage.removeItem('impersonatedName');
    localStorage.removeItem('postGreParentClubId');
    // Restore original platform token + refresh token, and clear the in-memory
    // access token cache — JwtAuthInterceptor checks AuthService.getAccessToken()
    // before localStorage['USER'].token, so a stale in-memory token (impersonated
    // admin's) would otherwise keep overriding the restored super user session.
    const superUserToken = localStorage.getItem('superUserToken');
    const superUserRefreshToken = localStorage.getItem('superUserRefreshToken');
    if (superUserToken) {
      localStorage.setItem('SECURE_TOKEN', superUserToken);
    }
    this.authService.setTokens(superUserToken || '', superUserRefreshToken || '');
    localStorage.removeItem('superUserRefreshToken');
    window.location.reload();
  }

  get impersonationText(): string {
    const name = localStorage.getItem('impersonatedName');
    const email = localStorage.getItem('impersonatedEmail');
    if (name || email) {
      const parts: string[] = [];
      if (name) parts.push(name);
      if (email) {
        const [local, domain] = email.split('@');
        parts.push(local.slice(0, 2) + '***@' + (domain || ''));
      }
      return 'Impersonating: ' + parts.join(' • ');
    }
    return 'Logged in as Super User';
  }
}